import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { AlertCircle, CheckCircle, Pause, Lock, AlertTriangle, MoreVertical, Phone, Mail, Globe, MapPin, Send } from 'lucide-react';

const AdminProviderPerformancePage = () => {
  const navigate = useNavigate();
  const { providerId: routeProviderId } = useParams<{ providerId: string }>();
  const [searchParams] = useSearchParams();
  const queryProviderId = searchParams.get('id');
  const [activeTab, setActiveTab] = useState('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchedData, setFetchedData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);

  // (audit 2026-06-10) — Admin Command sidebar buttons (Approve/Pause/etc.)
  // previously rendered without onClick. Now each delegates to the same
  // POST handler hitting the per-action endpoints under /admin/providers/:id/.
  const fetchData = async () => {
    try {
      const pidParam = queryProviderId || routeProviderId;
      const response = await apiService.request(`/admin/providers/performance${pidParam ? `?id=${pidParam}` : ''}`, 'GET');
      if (response.success && response.data) {
        setFetchedData(Array.isArray(response.data) ? response.data[0] : response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const adminAction = async (
    action: 'approve' | 'request-documents' | 'pause-payouts' | 'restrict-eligibility' | 'suspend',
    confirmMsg: string,
    successMsg: string,
  ) => {
    const pid = fetchedData?.id || fetchedData?.provider_id || routeProviderId;
    if (!pid) {
      alert('No provider selected.');
      return;
    }
    if (!window.confirm(confirmMsg)) return;
    setActionBusy(true);
    try {
      const res: any = await apiService.request(`/admin/providers/${pid}/${action}`, 'POST', {});
      if (res?.success) {
        alert(successMsg);
        await fetchData();
      } else {
        alert('Error: ' + (res?.message || 'Action failed'));
      }
    } catch (e: any) {
      alert('Failed: ' + (e?.message || 'Network error'));
    } finally {
      setActionBusy(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryProviderId]);

  const tabs = [
    { id: 'overview', label: 'Overview' },
  ];

  const providerName = fetchedData?.business_name || fetchedData?.company_name || fetchedData?.name || 'Provider';
  const providerInitials = providerName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || 'PR';
  const providerStatus = (fetchedData?.status || 'ACTIVE').toUpperCase();
  const providerRegistryId = fetchedData?.provider_id || fetchedData?.registry_id || fetchedData?.id || '—';
  const providerPhone = fetchedData?.phone || fetchedData?.contact_phone || '—';
  const providerEmail = fetchedData?.email || fetchedData?.contact_email || '—';
  const providerWebsite = fetchedData?.website || fetchedData?.website_url || '—';
  const providerCoverage = fetchedData?.coverage_area || fetchedData?.service_area || fetchedData?.location || '—';
  const providerVat = fetchedData?.vat_number || fetchedData?.vat || '—';
  const providerServices: string[] = fetchedData?.services || fetchedData?.service_types || [];

  return (
    <>
      <Helmet>
        <title>Provider Details - Virewire Admin</title>
      </Helmet>
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 mb-8 shadow-sm">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start gap-6">
                {/* Avatar */}
                <div className="w-20 h-20 bg-gradient-to-br from-teal-600 to-green-700 rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                  {providerInitials}
                </div>

                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">{loading ? 'Loading…' : providerName}</h1>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-bold text-xs uppercase tracking-wide ${providerStatus === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      ● {providerStatus}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">REGISTRY ID: #{providerRegistryId}</p>
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    {providerCoverage !== '—' && (
                      <span className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {providerCoverage}
                      </span>
                    )}
                    {providerVat !== '—' && (
                      <span className="flex items-center gap-2">
                        <span className="text-lg">✓</span>
                        VAT Registered: {providerVat}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const uid = fetchedData?.user_id || queryProviderId || routeProviderId;
                    if (uid) navigate(`/admin/providers/${uid}/edit`);
                    else navigate('/admin/providers');
                  }}
                  className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-6 rounded-lg transition-colors"
                >
                  Edit Profile
                </button>
                <button
                  onClick={() => navigate('/admin/providers')}
                  title="More providers"
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <MoreVertical className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center border-b border-gray-200 mb-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 font-medium text-sm transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-3 gap-8">
              <div className="col-span-2 space-y-8">
                {/* Contact Information */}
                <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h2>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Phone</p>
                        <p className="text-gray-900 font-medium">{providerPhone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Email</p>
                        <p className="text-gray-900 font-medium">{providerEmail}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Globe className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Website</p>
                        <p className="text-gray-900 font-medium">{providerWebsite}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Working Hours & Team Details Grid */}
                <div className="grid grid-cols-2 gap-8">
                  {/* Working Hours */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">Working Hours</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-600">Mon - Fri</p>
                        <p className="font-bold text-gray-900">08:00 - 18:00</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-600">Saturday</p>
                        <p className="font-bold text-gray-900">09:00 - 14:00</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-600">Sunday</p>
                        <p className="font-bold text-red-600 uppercase tracking-widest text-xs">Emergency Only</p>
                      </div>
                    </div>
                  </div>

                  {/* Team Details */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">Team Details</h3>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex -space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm border-2 border-white">
                          JB
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm border-2 border-white">
                          SM
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm border-2 border-white">
                          LS
                        </div>
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-gray-700 font-bold text-sm border-2 border-white">
                          +8
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">11 Active Engineers, 4 Support Staff</p>
                  </div>
                </div>

                {/* Services Offered */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Services Offered</h3>
                  <div className="flex flex-wrap gap-2">
                    {providerServices.length > 0 ? providerServices.map((service: string) => (
                      <span key={service} className="px-4 py-2 bg-teal-50 text-teal-700 rounded-lg font-bold text-sm border border-teal-200">
                        {service}
                      </span>
                    )) : (
                      <span className="text-sm text-gray-500">No services listed</span>
                    )}
                  </div>
                </div>

                {/* Service Area Map */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Service Area</h3>
                  <div className="bg-gradient-to-br from-teal-300 to-teal-600 rounded-xl h-40 flex items-center justify-center relative">
                    <div className="bg-white px-4 py-2 rounded-lg font-bold text-teal-600 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      South-East London Cluster
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Sidebar */}
              <div className="space-y-6">
                {/* Admin Command */}
                <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 shadow-sm">
                  <h3 className="text-xs font-bold text-teal-400 uppercase tracking-widest mb-4">Admin Command</h3>
                  <div className="space-y-3">
                    <button
                      disabled={actionBusy}
                      onClick={() => adminAction('approve', 'Approve this provider?', 'Provider approved')}
                      className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded-lg text-white transition-colors"
                    >
                      <span className="font-bold text-sm">Approve Provider</span>
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    </button>
                    <button
                      disabled={actionBusy}
                      onClick={() => adminAction('request-documents', 'Send a document-update request to this provider?', 'Request sent')}
                      className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded-lg text-white transition-colors"
                    >
                      <span className="font-bold text-sm">Request Document Update</span>
                      <AlertCircle className="w-4 h-4" />
                    </button>
                    <button
                      disabled={actionBusy}
                      onClick={() => adminAction('pause-payouts', 'Pause payouts for this provider?', 'Payouts paused')}
                      className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded-lg text-white transition-colors"
                    >
                      <span className="font-bold text-sm">Pause Payouts</span>
                      <Pause className="w-4 h-4" />
                    </button>
                    <button
                      disabled={actionBusy}
                      onClick={() => adminAction('restrict-eligibility', 'Restrict eligibility for this provider?', 'Eligibility restricted')}
                      className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded-lg text-white transition-colors"
                    >
                      <span className="font-bold text-sm">Restrict Eligibility</span>
                      <Lock className="w-4 h-4" />
                    </button>
                    <button
                      disabled={actionBusy}
                      onClick={() => adminAction('suspend', 'SUSPEND this provider account? They will lose access immediately.', 'Account suspended')}
                      className="w-full flex items-center justify-between p-3 bg-red-950 hover:bg-red-900 disabled:opacity-50 rounded-lg text-red-400 transition-colors"
                    >
                      <span className="font-bold text-sm">Suspend Account</span>
                      <AlertTriangle className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Recent Governance Logs */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">Recent Governance Logs</h3>
                  <div className="space-y-4">
                    <div className="border-l-4 border-teal-500 pl-4">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">AL</p>
                      <p className="font-bold text-gray-900 text-sm">Account Level Upgrade</p>
                      <p className="text-xs text-gray-600">Automated • 2 hours ago</p>
                    </div>
                    <div className="border-l-4 border-blue-500 pl-4">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">DC</p>
                      <p className="font-bold text-gray-900 text-sm">Public Liability Ins. Verified</p>
                      <p className="text-xs text-gray-600">Admin Sarah K. • Yesterday</p>
                    </div>
                    <div className="border-l-4 border-green-500 pl-4">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">PY</p>
                      <p className="font-bold text-gray-900 text-sm">Payout Cycle Completed</p>
                      <p className="text-xs text-gray-600">System Process • 3 days ago</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const pid = fetchedData?.id || fetchedData?.provider_id || routeProviderId;
                      if (pid) navigate(`/admin/users/${pid}/activity`);
                      else alert('No provider selected');
                    }}
                    className="text-teal-600 hover:text-teal-900 font-bold text-xs uppercase tracking-widest mt-4"
                  >
                    View Full Audit Log
                  </button>
                </div>

                {/* Certification Warning */}
                <div className="bg-red-50 border border-red-300 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-bold text-red-900 text-sm">Certification Expiring</p>
                      <p className="text-xs text-red-700 mt-1">Gas Safe Register (ID: 88219) expires in 14 days. Automated notification sent.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </AdminLayout>
    </>
  );
};

export default AdminProviderPerformancePage;
